// src/services/holidayPlan.service.js
import { api } from './api';

class HolidayPlanService {
    /**
     * Get all holiday plans
     */
    async getHolidayPlans() {
        try {
            const response = await api.get('/api/holiday-plans');
            return response.data.plans || [];
        } catch (error) {
            console.error('Error fetching holiday plans:', error);
            throw error;
        }
    }

    /**
     * Get specific holiday plan with details
     * @param {number} id - Plan ID
     * @param {number} year - Optional year to filter holidays
     */
    async getHolidayPlanById(id, year = null) {
        try {
            let url = `/api/holiday-plans/${id}`;
            if (year) {
                url += `?year=${year}`;
            }
            const response = await api.get(url);
            return response.data.plan;
        } catch (error) {
            console.error('Error fetching holiday plan:', error);
            throw error;
        }
    }

    // ==================== MULTI-YEAR HOLIDAY PLAN METHODS ====================

    /**
     * Get available years for a holiday plan
     * Returns array of year numbers
     */
    async getYearsForPlan(planId) {
        try {
            const response = await api.get(`/api/holiday-plans/${planId}/years`);
            // Backend returns [{ year, holidayCount }], extract just the years
            const years = response.data.years || [];
            return years.map(y => typeof y === 'object' ? y.year : y);
        } catch (error) {
            console.error('Error fetching years for plan:', error);
            throw error;
        }
    }

    /**
     * Copy holidays from one year to another
     */
    async copyHolidaysToYear(planId, sourceYear, targetYear) {
        try {
            const response = await api.post(`/api/holiday-plans/${planId}/copy-year`, {
                sourceYear,
                targetYear
            });
            return response.data;
        } catch (error) {
            console.error('Error copying holidays:', error);
            throw error;
        }
    }

    /**
     * Add holidays for a specific year
     */
    async addHolidaysForYear(planId, year, holidays) {
        try {
            const response = await api.post(`/api/holiday-plans/${planId}/years/${year}/holidays`, {
                holidays
            });
            return response.data;
        } catch (error) {
            console.error('Error adding holidays for year:', error);
            throw error;
        }
    }

    /**
     * Delete all holidays for a specific year
     */
    async deleteHolidaysForYear(planId, year) {
        try {
            const response = await api.delete(`/api/holiday-plans/${planId}/years/${year}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting holidays for year:', error);
            throw error;
        }
    }

    /**
     * Fetch country holidays from API or hardcoded data
     */
    async fetchCountryHolidays(countryCode, year) {
        try {
            const response = await api.get(`/api/holiday-plans/country/${countryCode}/${year}`);
            return response.data.holidays || [];
        } catch (error) {
            console.error('Error fetching country holidays:', error);
            throw error;
        }
    }


    /**
     * Create holiday plan
     */
    async createHolidayPlan(planData) {
        try {
            const response = await api.post('/api/holiday-plans', planData);
            return response.data.plan;
        } catch (error) {
            console.error('Error creating holiday plan:', error);
            throw error;
        }
    }

    /**
     * Update holiday plan
     */
    async updateHolidayPlan(id, planData) {
        try {
            const response = await api.put(`/api/holiday-plans/${id}`, planData);
            return response.data;
        } catch (error) {
            console.error('Error updating holiday plan:', error);
            throw error;
        }
    }

    /**
     * Delete holiday plan
     */
    async deleteHolidayPlan(id) {
        try {
            const response = await api.delete(`/api/holiday-plans/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting holiday plan:', error);
            throw error;
        }
    }

    /**
     * Assign employees to holiday plan
     */
    async assignEmployees(planId, employeeIds) {
        try {
            const response = await api.post(`/api/holiday-plans/${planId}/assign-employees`, { employeeIds });
            return response.data;
        } catch (error) {
            console.error('Error assigning employees:', error);
            throw error;
        }
    }

    /**
     * Add holiday to plan
     */
    async addHolidayToPlan(planId, holidayData) {
        try {
            const response = await api.post(`/api/holiday-plans/${planId}/holidays`, holidayData);
            return response.data.holiday;
        } catch (error) {
            console.error('Error adding holiday:', error);
            throw error;
        }
    }

    /**
     * Update holiday
     */
    async updateHoliday(holidayId, holidayData) {
        try {
            const response = await api.put(`/api/holiday-plans/holidays/${holidayId}`, holidayData);
            return response.data.holiday;
        } catch (error) {
            console.error('Error updating holiday:', error);
            throw error;
        }
    }

    /**
     * Delete holiday
     */
    async deleteHoliday(holidayId) {
        try {
            const response = await api.delete(`/api/holiday-plans/holidays/${holidayId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting holiday:', error);
            throw error;
        }
    }

    // ==================== OPTIONAL HOLIDAY REQUEST METHODS ====================

    /**
     * Get optional holidays available for employee to request
     */
    async getAvailableOptionalHolidays() {
        try {
            const response = await api.get('/api/holiday-plans/optional-holidays/available');
            return response.data.optionalHolidays || [];
        } catch (error) {
            console.error('Error fetching optional holidays:', error);
            throw error;
        }
    }

    /**
     * Get employee's own optional holiday requests
     */
    async getMyOptionalHolidayRequests() {
        try {
            const response = await api.get('/api/holiday-plans/optional-holidays/my-requests');
            return response.data.requests || [];
        } catch (error) {
            console.error('Error fetching my requests:', error);
            throw error;
        }
    }

    /**
     * Request an optional holiday
     */
    async requestOptionalHoliday(holidayDateId) {
        try {
            const response = await api.post('/api/holiday-plans/optional-holidays/request', { holidayDateId });
            return response.data;
        } catch (error) {
            console.error('Error requesting optional holiday:', error);
            throw error;
        }
    }

    /**
     * Cancel a pending optional holiday request
     */
    async cancelOptionalHolidayRequest(requestId) {
        try {
            const response = await api.delete(`/api/holiday-plans/optional-holidays/requests/${requestId}`);
            return response.data;
        } catch (error) {
            console.error('Error cancelling request:', error);
            throw error;
        }
    }

    /**
     * Admin: Get all optional holiday requests for company
     */
    async getOptionalHolidayRequests(status = null) {
        try {
            const url = status
                ? `/api/holiday-plans/optional-holidays/requests?status=${status}`
                : '/api/holiday-plans/optional-holidays/requests';
            const response = await api.get(url);
            return response.data.requests || [];
        } catch (error) {
            console.error('Error fetching optional holiday requests:', error);
            throw error;
        }
    }

    /**
     * Admin: Approve optional holiday request
     */
    async approveOptionalHoliday(requestId) {
        try {
            const response = await api.post(`/api/holiday-plans/optional-holidays/requests/${requestId}/approve`);
            return response.data;
        } catch (error) {
            console.error('Error approving request:', error);
            throw error;
        }
    }

    /**
     * Admin: Reject optional holiday request
     */
    async rejectOptionalHoliday(requestId, reason = null) {
        try {
            const response = await api.post(`/api/holiday-plans/optional-holidays/requests/${requestId}/reject`, { reason });
            return response.data;
        } catch (error) {
            console.error('Error rejecting request:', error);
            throw error;
        }
    }

    /**
     * Get approved optional holidays for an employee (used by payroll)
     */
    async getApprovedOptionalHolidays(employeeId, startDate, endDate) {
        try {
            let url = `/api/holiday-plans/optional-holidays/approved/${employeeId}`;
            if (startDate && endDate) {
                url += `?startDate=${startDate}&endDate=${endDate}`;
            }
            const response = await api.get(url);
            return response.data.holidays || [];
        } catch (error) {
            console.error('Error fetching approved optional holidays:', error);
            throw error;
        }
    }
}

export default new HolidayPlanService();
